import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {UserAccount} from "@/app/identity-access-management/authentication/model/user-account.ts";
import {
    AuthState,
    clearStoredUserAccount,
    persistUserAccount,
    readAuthStateFromBackend,
    URL_LOGIN,
    URL_LOGOUT,
} from "@/app/identity-access-management/authentication/context/auth-state.ts";

type AuthContextValue = {
    state: AuthState;
    login: (email: string, password: string) => Promise<void>;
    logout: (options?: LogoutOptions) => Promise<void>;
    refresh: () => Promise<void>; // re-check session
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
    children: React.ReactNode;
    initialState?: AuthState;
};

type AuthError = Error & {
    status?: number;
};

type LogoutOptions = {
    redirect?: boolean;
    clearLocalSession?: boolean;
};

export function AuthProvider({
    children,
    initialState = { status: "loading", userAccount: null },
}: AuthProviderProps) {
    const [state, setState] = useState<AuthState>(initialState);
    const authRequestVersionRef = useRef(0);

    function setAnonymousState() {
        clearStoredUserAccount();
        setState({ status: "anonymous", userAccount: null });
    }

    async function refresh() {
        const requestVersion = ++authRequestVersionRef.current;

        try {
            const nextState = await readAuthStateFromBackend();
            if (requestVersion !== authRequestVersionRef.current) {
                return;
            }
            setState(nextState);
        } catch {
            if (requestVersion !== authRequestVersionRef.current) {
                return;
            }
            setAnonymousState();
        }
    }

    async function login(email: string, password: string) {
        // Invalidiert ältere refresh()-Requests, damit sie den Login-State nicht überschreiben.
        authRequestVersionRef.current += 1;

        const response = await fetch(URL_LOGIN, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });
        if (response.ok) {
            const userAccount: UserAccount = await response.json();
            persistUserAccount(userAccount);
            setState({ status: "authenticated", userAccount });
        } else {
            // Fehler werfen, damit der catch-Block im Aufrufer erreicht wird
            const errorText = await response.text();
            const error: AuthError = new Error(
                response.status === 401
                    ? "NOT_AUTHORIZED"
                    : errorText || "Login fehlgeschlagen"
            );
            // Statuscode als Property hinzufügen
            error.status = response.status;
            throw error;
        }
    }

    async function logout(options: LogoutOptions = {}) {
        const {
            redirect = true,
            clearLocalSession = false,
        } = options;

        const response = await fetch(URL_LOGOUT, {
            method: "POST",
            credentials: "include",
        });

        if (response.ok) {
            setAnonymousState();
            if (redirect) {
                window.location.assign("/");
            }
        } else {
            if (clearLocalSession) {
                setAnonymousState();
                if (redirect) {
                    window.location.assign("/");
                }
                return;
            }

            console.error("Logout fehlgeschlagen");
        }
    }

    useEffect(() => {
        if (initialState.status === "loading") {
            void refresh();
        }
    }, [initialState.status]);

    const value = { state, login, logout, refresh };

    return <AuthContext.Provider value={value}>
        {children}
    </AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
